import { useTranslation } from "react-i18next";
import { Box, IconButton, Typography, Snackbar, Alert } from "@mui/material";
import { useLocation } from "react-router-dom";

import { CopyIcon, EmailIcon, FacebookIcon, TwitterIcon, WhatsAppIcon } from "../../assets/icons/go-hire-icons";
import { useState } from "react";

import { styles } from "./styles";

export const ShareService = () => {
  const { t } = useTranslation("goService", { keyPrefix: "servicePage" });
  const { i18n } = useTranslation();
  const currentLanguage = i18n.language;
  const location = useLocation();
  const [copied, setCopied] = useState(false);
  const [shareError, setShareError] = useState<string | null>(null);

  const currentUrl = window.location.origin + location.pathname;
  const shareTitle = t("title");
  const shareDescription = t("description");

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(currentUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error("Failed to copy!", err);
      setShareError("Failed to copy to clipboard");
      setTimeout(() => setShareError(null), 3000);
    }
  };

  const handleFacebookShare = () => {
    try {
      const width = 550;
      const height = 450;
      const left = (window.screen.width - width) / 2;
      const top = (window.screen.height - height) / 2;

      window.open(
        `https://www.facebook.com/sharer.php?u=${encodeURIComponent(currentUrl)}`,
        "facebook-share-dialog",
        `width=${width},height=${height},left=${left},top=${top},toolbar=no,menubar=no,location=no,status=no`
      );
    } catch (err) {
      console.error("Failed to share on Facebook:", err);
      setShareError("Failed to share on Facebook");
      setTimeout(() => setShareError(null), 3000);
    }
  };

  const handleTwitterShare = () => {
    try {
      const tweetText = `${shareTitle}\n${shareDescription}`;
      const width = 550;
      const height = 450;
      const left = (window.screen.width - width) / 2;
      const top = (window.screen.height - height) / 2;

      window.open(
        `https://twitter.com/intent/tweet?text=${encodeURIComponent(tweetText)}&url=${encodeURIComponent(currentUrl)}`,
        "twitter-share-dialog",
        `width=${width},height=${height},left=${left},top=${top},toolbar=no,menubar=no,location=no,status=no`
      );
    } catch (err) {
      setShareError("Failed to share on Twitter");
      setTimeout(() => setShareError(null), 3000);
    }
  };

  const handleEmailShare = () => {
    try {
      const subject = shareTitle;
      const body = `${shareDescription}\n\n${currentUrl}`;
      window.location.href = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    } catch (err) {
      setShareError("Failed to open email client");
      setTimeout(() => setShareError(null), 3000);
    }
  };

  const handleWhatsAppShare = () => {
    try {
      const text = `${shareTitle}\n${shareDescription}\n${currentUrl}`;
      const width = 550;
      const height = 450;
      const left = (window.screen.width - width) / 2;
      const top = (window.screen.height - height) / 2;

      window.open(
        `https://wa.me/?text=${encodeURIComponent(text)}`,
        "whatsapp-share-dialog",
        `width=${width},height=${height},left=${left},top=${top},toolbar=no,menubar=no,location=no,status=no`
      );
    } catch (err) {
      setShareError("Failed to share on WhatsApp");
      setTimeout(() => setShareError(null), 3000);
    }
  };

  return (
    <Box sx={styles.shareContainer}>
      <Typography sx={styles.shareTitle}>{t("shareThisService")}</Typography>
      <Box sx={styles.shareButtonsContainer}>
        <IconButton sx={styles.shareButton} onClick={handleCopy} color={copied ? "primary" : "default"}>
          <CopyIcon />
        </IconButton>
        <IconButton
          sx={{
            ...styles.shareButton,
            ...(currentLanguage === "ar"
              ? {
                  "& .MuiSvgIcon-root": {
                    transform: "scaleX(1) !important",
                  },
                }
              : {}),
          }}
          onClick={handleWhatsAppShare}
        >
          <FacebookIcon />
        </IconButton>
        <IconButton
          sx={{
            ...styles.shareButton,
            ...(currentLanguage === "ar"
              ? {
                  "& .MuiSvgIcon-root": {
                    transform: "scaleX(1) !important",
                  },
                }
              : {}),
          }}
          onClick={handleWhatsAppShare}
        >
          <TwitterIcon />
        </IconButton>
        <IconButton sx={styles.shareButton} onClick={handleEmailShare}>
          <EmailIcon />
        </IconButton>
        <IconButton
          sx={{
            ...styles.shareButton,
            ...(currentLanguage === "ar"
              ? {
                  "& .MuiSvgIcon-root": {
                    transform: "scaleX(1) !important",
                  },
                }
              : {}),
          }}
          onClick={handleWhatsAppShare}
        >
          <WhatsAppIcon />
        </IconButton>
      </Box>
      <Snackbar
        open={!!shareError}
        autoHideDuration={3000}
        onClose={() => setShareError(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity="error" onClose={() => setShareError(null)}>
          {shareError}
        </Alert>
      </Snackbar>
    </Box>
  );
};
